import {
  REALTIME_PATH,
  canReconnect,
  isRealtimeFrame,
  type RealtimeTopic,
} from '../realtime/protocol'

/**
 * The client half of the realtime socket: connect, subscribe, reconnect, give up.
 *
 * Framework-free on purpose — it takes callbacks and returns three methods, so the hook stays
 * about *what to do with a phase change* and this file about *keeping a socket alive*. It is also
 * why the hook is testable: inject a fake socket and drive opens, frames and drops by hand.
 *
 * A `WebSocket` does not reconnect, so this carries a backoff ladder (1s doubling to 30s, reset on
 * a clean open) and, after two failed *connections*, a permanent handover to the caller's fallback
 * poll — deliberately the same shape `useEventFeed` walks for the backoffice feed.
 *
 * **A close with code 1008 ends the ladder instead of feeding it.** A client refused for policy
 * would otherwise reconnect, be refused, and repeat forever as fast as the backoff allows.
 */

/** The slice of `WebSocket` used here, so a fake needs five members rather than the whole API. */
export interface WebSocketLike {
  send(data: string): void
  close(code?: number, reason?: string): void
  onopen: (() => void) | null
  onmessage: ((event: { data: unknown }) => void) | null
  onclose: ((event: { code: number }) => void) | null
  onerror: (() => void) | null
}

export type RealtimeStatus = 'idle' | 'connecting' | 'live' | 'polling' | 'refused'

/** First retry delay, then doubling. */
export const RECONNECT_BASE_MS = 1_000
export const RECONNECT_CAP_MS = 30_000
/**
 * Two failed handshakes **before any socket has ever opened** hands the client to the poll. Two is
 * enough: a third attempt against a proxy missing its Upgrade headers fails identically.
 *
 * Deliberately scoped to the *first* connection. Once a socket has opened once, the path is known
 * to work, and a later outage is a redeploy or a flaky network — that retries with backoff
 * forever rather than demoting the client to 4-second polling for the rest of its session.
 */
export const MAX_CONNECT_FAILURES = 2

export interface RealtimeConnectionOptions<T> {
  topic: RealtimeTopic
  /** Applied on every frame, including the one that answers `subscribe`. */
  onFrame: (payload: T) => void
  /** The ladder has given up. The caller starts its fallback poll; this connection is done. */
  onFallback: () => void
  onStatus?: (status: RealtimeStatus) => void
  /** Injected by the tests; production uses the real `WebSocket`. */
  socketFactory?: (url: string) => WebSocketLike
  /** Injected for the same reason — returns its own cancel. */
  schedule?: (run: () => void, ms: number) => () => void
  /** Where to dial. Defaults to the page's own origin; injected by the tests, which have no page. */
  location?: { protocol: string; host: string }
}

export interface RealtimeConnection {
  /** Opens the socket. A no-op while one is already open or opening. */
  open: () => void
  /** Drops the socket without ending the ladder — what `visibilitychange` to hidden calls. */
  suspend: () => void
  /** Permanent teardown: no reconnect, no fallback. */
  stop: () => void
}

/** `wss:` on https, `ws:` on http — same origin either way, so the session cookie rides along. */
export function realtimeUrl(location: { protocol: string; host: string }): string {
  const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${scheme}//${location.host}${REALTIME_PATH}`
}

export function createRealtimeConnection<T>(
  options: RealtimeConnectionOptions<T>
): RealtimeConnection {
  const schedule =
    options.schedule ??
    ((run, ms) => {
      const id = setTimeout(run, ms)
      return () => clearTimeout(id)
    })
  const socketFactory =
    options.socketFactory ?? ((url: string) => new WebSocket(url) as unknown as WebSocketLike)

  let socket: WebSocketLike | null = null
  let cancelRetry: (() => void) | null = null
  let failures = 0
  /** Whether any socket on this connection has ever completed a handshake. See
   * MAX_CONNECT_FAILURES. */
  let everOpened = false
  let delay = RECONNECT_BASE_MS
  /** Highest `seq` applied, echoed as `since` so a reconnect says where it got to. */
  let since = 0
  let stopped = false
  let suspended = false

  const setStatus = (status: RealtimeStatus) => options.onStatus?.(status)

  const clearRetry = () => {
    cancelRetry?.()
    cancelRetry = null
  }

  const detach = () => {
    if (!socket) return
    socket.onopen = null
    socket.onmessage = null
    socket.onclose = null
    socket.onerror = null
    const closing = socket
    socket = null
    try {
      closing.close()
    } catch {
      // Already closing. Nothing to do — the handlers are detached either way.
    }
  }

  const giveUp = () => {
    stopped = true
    clearRetry()
    detach()
    setStatus('polling')
    options.onFallback()
  }

  const retry = () => {
    if (stopped || suspended || cancelRetry) return
    const wait = delay
    delay = Math.min(delay * 2, RECONNECT_CAP_MS)
    cancelRetry = schedule(() => {
      cancelRetry = null
      connect()
    }, wait)
  }

  /**
   * One place for "the socket went away", however it went.
   *
   * The fallback is about a path that has **never** worked — a proxy that eats Upgrade headers,
   * which fails as a clean degrade and so will not look broken. An outage on a connection that has
   * opened before is a redeploy or a flaky network, and that retries indefinitely: demoting a
   * healthy client to 4-second polling for the rest of its session because the server took three
   * seconds to come back would be the ladder misfiring on the one case it is not for.
   */
  const dropped = (code: number) => {
    if (stopped) return
    detach()
    if (!canReconnect(code)) {
      // Refused on policy: no reconnect, and no poll either — the poll would be refused too.
      stopped = true
      clearRetry()
      setStatus('refused')
      return
    }
    if (!everOpened) {
      failures += 1
      if (failures >= MAX_CONNECT_FAILURES) {
        giveUp()
        return
      }
    }
    setStatus('connecting')
    retry()
  }

  function connect(): void {
    if (stopped || suspended || socket) return
    setStatus('connecting')
    let next: WebSocketLike
    try {
      next = socketFactory(realtimeUrl(options.location ?? window.location))
    } catch {
      // A constructor that throws (a blocked URL scheme, a locked-down browser) is a handshake
      // that never happened — count it and let the ladder decide.
      dropped(1006)
      return
    }
    socket = next

    next.onopen = () => {
      if (socket !== next) return
      everOpened = true
      failures = 0
      // The ladder starts over from one second after every clean open, so a long outage backs off
      // to 30s but the reconnect *after* it does not inherit that delay.
      delay = RECONNECT_BASE_MS
      setStatus('live')
      next.send(JSON.stringify({ type: 'subscribe', topic: options.topic, since }))
    }

    next.onmessage = (event) => {
      if (socket !== next) return
      let parsed: unknown
      try {
        parsed = JSON.parse(String(event.data))
      } catch {
        return
      }
      if (!isRealtimeFrame(parsed) || parsed.topic !== options.topic) return
      // `since` moves, but a frame is never dropped for repeating one: the server answers a
      // re-subscribe with current state at the *current* seq, and de-duplication is the caller's
      // job via `phaseSignature`. Dropping here would cost the self-heal a reconnect exists for.
      if (parsed.seq > since) since = parsed.seq
      options.onFrame(parsed.payload as T)
    }

    next.onclose = (event) => {
      if (socket !== next) return
      dropped(event?.code ?? 1006)
    }

    // `onerror` carries no code and is always followed by a close, so it is left to `onclose` —
    // counting both would burn the two-failure budget on a single failed handshake.
    next.onerror = () => {}
  }

  return {
    open: () => {
      suspended = false
      if (stopped) return
      connect()
    },
    suspend: () => {
      suspended = true
      clearRetry()
      detach()
      setStatus('idle')
    },
    stop: () => {
      stopped = true
      clearRetry()
      detach()
    },
  }
}
